import {
  type Principle,
  proofreadKey,
} from "@repo/fika-blog/src/extensions/proofread"
import type { Editor } from "@repo/fika-blog/src/tiptap/core"
import type { AccentHue } from "@repo/fika-design-system/color"
import { createMemo, Show } from "solid-js"
import { createEditorTransaction } from "solid-tiptap"
import { Mini } from "@/designSystem/components/mini/mini"
import { Tooltip } from "@/designSystem/components/tooltip"
import { proposalClass } from "./proofread-menu.css"

type Props = {
  editor: Editor
}

interface PluginState {
  storedDecorations: Array<{
    id: string
    from: number
    to: number
    originalText: string
    modifiedText: string
    principle: Principle
  }>
  activeId: string | null
}

const PRINCIPLE_HUE: Record<Principle, AccentHue> = {
  grammar: "emerald",
  tighten: "orange",
  refine: "indigo",
}

export function ProofreadMenu(props: Props) {
  const selection = createEditorTransaction(
    () => props.editor,
    editor => editor?.state.selection,
  )

  const decorationInfo = createMemo(() => {
    const sel = selection()
    if (!sel || !sel.empty) return null

    const state = props.editor.state
    const pluginState = proofreadKey.getState(state) as PluginState | undefined
    if (!pluginState || !pluginState.activeId) return null

    return pluginState.storedDecorations.find(
      d => d.id === pluginState.activeId,
    )
  })

  function acceptChange() {
    const info = decorationInfo()
    if (!info) return

    props.editor.chain().focus().acceptProofreadChange(info).run()
  }

  function rejectChange() {
    const info = decorationInfo()
    if (!info) return

    props.editor.chain().focus().removeProofreadDecoration(info.id).run()
  }

  function hue(principle: Principle) {
    return PRINCIPLE_HUE[principle]
  }

  return (
    <Show when={decorationInfo()}>
      {info => (
        <Mini.Menu>
          <Mini.Section>
            <Tooltip description="accept" hue={hue(info().principle)}>
              <Mini.Button
                onClick={acceptChange}
                iconName="check"
                title={
                  <Show
                    when={info().modifiedText === ""}
                    fallback={
                      <span class={proposalClass()}>{info().modifiedText}</span>
                    }
                  >
                    <span class={proposalClass({ delete: true })}>
                      {info().originalText}
                    </span>
                  </Show>
                }
                hue={hue(info().principle)}
              />
            </Tooltip>
            <Tooltip description="reject" hue="rose">
              <Mini.Button onClick={rejectChange} iconName="x" hue="rose" />
            </Tooltip>
          </Mini.Section>
        </Mini.Menu>
      )}
    </Show>
  )
}
